import React, { useCallback, useEffect, useState } from "react";
import Header from "../components/Header";
import { pageColors } from "../mock";
import eventsMark from "../assets/nav_events.png";
import { useAuth } from "../context/AuthContext";
import EventsMap from "../components/EventsMap";
import DateTimePicker from "../components/DateTimePicker";
import { uploadFile } from "../lib/uploads";
import {
  Calendar,
  MapPin,
  Plus,
  Trash2,
  Users,
  Upload,
  X,
  Edit3,
  Share2,
  Check,
  ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title: "",
  description: "",
  location: "",
  venue: "",
  organizers: "",
  starts_date: "",
  starts_time: "",
  ends_date: "",
  ends_time: "",
  link: "",
  hero_image_url: null,
};

const splitDateTime = (iso) => {
  if (!iso) return { date: "", time: "" };
  // Backend stores strings like "2026-08-22T11:00" — split cleanly.
  const [date = "", time = ""] = iso.split("T");
  return { date, time: time.slice(0, 5) };
};

const combineDateTime = (date, time) => {
  if (!date || !time) return "";
  return `${date}T${time}`;
};

const isPastEvent = (ev) => {
  const end = ev.ends_at || ev.starts_at;
  if (!end) return false;
  const t = new Date(end).getTime();
  return !Number.isNaN(t) && t < Date.now();
};

const Events = () => {
  const { current, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [editingId, setEditingId] = useState(null); // "new" | event.id | null
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState("");

  useEffect(() => {
    document.title = "TMM|EVENTS";
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/events`);
      const d = await r.json();
      setEvents(d.events || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Scroll to an event card if the URL has a #id hash (used by shared links).
  useEffect(() => {
    if (!events.length) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(`event-${hash}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("events-card-highlight");
      setTimeout(() => el.classList.remove("events-card-highlight"), 2400);
    }
  }, [events]);

  const canHost = current && current.status === "approved";
  const isEditing = !!editingId;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startNew = () => {
    setForm(emptyForm);
    setEditingId("new");
    setErr("");
  };

  const startEdit = (ev) => {
    const s = splitDateTime(ev.starts_at || "");
    const e = splitDateTime(ev.ends_at || "");
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      location: ev.location || "",
      venue: ev.venue || "",
      organizers: ev.organizers || "",
      starts_date: s.date,
      starts_time: s.time,
      ends_date: e.date,
      ends_time: e.time,
      link: ev.link || "",
      hero_image_url: ev.hero_image_url || null,
    });
    setEditingId(ev.id);
    setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file, token);
      setForm((f) => ({ ...f, hero_image_url: up.url }));
    } catch (ex) {
      setErr(ex.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim() || !form.location.trim()) {
      setErr("Title and address are required.");
      return;
    }
    if (!form.starts_date || !form.starts_time) {
      setErr("Please pick a start date and time.");
      return;
    }
    if (!form.ends_date || !form.ends_time) {
      setErr("Please pick an end date and time.");
      return;
    }
    const starts_at = combineDateTime(form.starts_date, form.starts_time);
    const ends_at = combineDateTime(form.ends_date, form.ends_time);
    if (new Date(ends_at) <= new Date(starts_at)) {
      setErr("End time must be after the start time.");
      return;
    }
    setBusy(true);
    try {
      const isNew = editingId === "new";
      const url = isNew ? `${API}/api/events` : `${API}/api/events/${editingId}`;
      const method = isNew ? "POST" : "PATCH";
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        venue: form.venue,
        organizers: form.organizers,
        starts_at,
        ends_at,
        link: form.link,
        hero_image_url: form.hero_image_url,
      };
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to save event.");
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  const rsvp = async (id) => {
    if (!token) return;
    setBusy(true);
    await fetch(`${API}/api/events/${id}/rsvp`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
    setBusy(false);
  };

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    setBusy(true);
    await fetch(`${API}/api/events/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
    setBusy(false);
  };

  const share = async (ev) => {
    const url = `${window.location.origin}/events#${ev.id}`;
    const shareData = {
      title: ev.title,
      text: `${ev.title} — ${ev.location_display || ev.location}`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* user cancelled; fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(ev.id);
      setTimeout(() => setCopiedId(""), 1500);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const addressQuery = (ev) => {
    const parts = [ev.venue, ev.location_display || ev.location].filter(Boolean);
    return encodeURIComponent(parts.join(", "));
  };

  return (
    <>
      <Header hoverColor={pageColors.events} compact={false} />
      <div className="page-mark-hero">
        <img src={eventsMark} alt="Events" className="page-mark-hero-img" />
      </div>

      <div className="events-page">
        <div className="events-lead">
          <p>
            Members throw events. Pin one on the map and drop by whatever's
            near you.
          </p>
          {canHost && !isEditing && (
            <button
              className="btn-solid"
              onClick={startNew}
              data-testid="events-toggle-form"
            >
              <Plus size={16} /> Host an event
            </button>
          )}
        </div>

        {isEditing && canHost && (
          <form className="events-form" onSubmit={submit} data-testid="events-form">
            <div className="apply-row">
              <label className="auth-field">
                <span>Title*</span>
                <input value={form.title} onChange={set("title")} data-testid="events-title" required />
              </label>
              <label className="auth-field">
                <span>Venue (optional)</span>
                <input value={form.venue} onChange={set("venue")} placeholder="Club, gallery, park…" />
              </label>
            </div>
            <label className="auth-field">
              <span>Address / city*</span>
              <input
                value={form.location}
                onChange={set("location")}
                placeholder="Albuquerque, USA"
                data-testid="events-location"
                required
              />
            </label>
            <label className="auth-field">
              <span>Organizers / branding (optional)</span>
              <input
                value={form.organizers}
                onChange={set("organizers")}
                placeholder="Sponsor names, partner brands"
                data-testid="events-organizers"
              />
            </label>
            <div className="apply-row">
              <label className="auth-field">
                <span>Starts*</span>
                <DateTimePicker
                  date={form.starts_date}
                  time={form.starts_time}
                  onChange={({ date, time }) =>
                    setForm((f) => ({ ...f, starts_date: date, starts_time: time }))
                  }
                  testIdPrefix="events-starts"
                  label="Pick start date & time"
                />
              </label>
              <label className="auth-field">
                <span>Ends*</span>
                <DateTimePicker
                  date={form.ends_date}
                  time={form.ends_time}
                  onChange={({ date, time }) =>
                    setForm((f) => ({ ...f, ends_date: date, ends_time: time }))
                  }
                  testIdPrefix="events-ends"
                  label="Pick end date & time"
                />
              </label>
            </div>
            <label className="auth-field">
              <span>Description</span>
              <textarea rows={4} value={form.description} onChange={set("description")} data-testid="events-description" />
            </label>
            <label className="auth-field">
              <span>Link (optional)</span>
              <input value={form.link} onChange={set("link")} placeholder="https://…" data-testid="events-link" />
            </label>

            <div className="apply-upload-block">
              <div className="apply-upload-label">Cover / hero image</div>
              <label className="btn-ghost apply-upload-btn" data-testid="events-hero-upload">
                <Upload size={16} /> {form.hero_image_url ? "Replace" : "Upload"}
                <input type="file" accept="image/*" hidden onChange={onHero} />
              </label>
              {form.hero_image_url && (
                <div className="apply-hero-preview" data-testid="events-hero-preview">
                  <img src={form.hero_image_url} alt="hero" />
                  <button type="button" className="chip-x" onClick={() => setForm((f) => ({ ...f, hero_image_url: null }))} aria-label="remove hero">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {err && <div className="auth-error" data-testid="events-form-error">{err}</div>}
            <div className="events-form-actions">
              <button type="submit" className="btn-solid" disabled={busy} data-testid="events-submit">
                {busy ? "…" : editingId === "new" ? "Publish event" : "Save changes"}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <EventsMap events={events} />

        <section className="events-list-section">
          <h2 className="events-list-title">Upcoming</h2>
          {events.filter((ev) => !isPastEvent(ev)).length === 0 ? (
            <div className="events-empty">No events yet — be the first to host one.</div>
          ) : (
            <div className="events-list">
              {events.filter((ev) => !isPastEvent(ev)).map((ev) => {
                const isMine = current && ev.host_user_id === current.id;
                const canEdit = isMine || current?.is_admin;
                return (
                  <article
                    key={ev.id}
                    id={`event-${ev.id}`}
                    className="events-card"
                    data-testid={`events-card-${ev.id}`}
                  >
                    {ev.hero_image_url && (
                      <div className="events-card-cover">
                        <img src={ev.hero_image_url} alt={ev.title} />
                      </div>
                    )}
                    <div className="events-card-when">
                      <Calendar size={14} /> {fmt(ev.starts_at)}
                      {ev.ends_at ? ` – ${fmt(ev.ends_at)}` : ""}
                    </div>
                    <div className="events-card-title">{ev.title}</div>
                    <a
                      className="events-card-where"
                      href={`https://www.google.com/maps/search/?api=1&query=${addressQuery(ev)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`events-address-${ev.id}`}
                    >
                      <MapPin size={13} />
                      <span>{ev.venue ? `${ev.venue} · ` : ""}{ev.location_display || ev.location}</span>
                    </a>
                    {ev.organizers && (
                      <div className="events-card-organizers" data-testid={`events-organizers-${ev.id}`}>
                        <b>Organizers:</b> {ev.organizers}
                      </div>
                    )}
                    {ev.description && <p className="events-card-desc">{ev.description}</p>}
                    <div className="events-card-actions">
                      {current ? (
                        <button
                          className="btn-solid"
                          disabled={busy}
                          onClick={() => rsvp(ev.id)}
                          data-testid={`events-rsvp-${ev.id}`}
                        >
                          <Users size={14} /> RSVP ({ev.rsvp_count})
                        </button>
                      ) : (
                        <span className="events-card-meta">Sign in to RSVP · {ev.rsvp_count} going</span>
                      )}
                      {ev.link && (
                        <a
                          className="btn-ghost"
                          href={ev.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`events-link-${ev.id}`}
                        >
                          <ExternalLink size={14} /> More info
                        </a>
                      )}
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => share(ev)}
                        data-testid={`events-share-${ev.id}`}
                      >
                        {copiedId === ev.id ? <><Check size={14} /> Copied</> : <><Share2 size={14} /> Share</>}
                      </button>
                      {canEdit && (
                        <>
                          <button
                            className="btn-ghost"
                            onClick={() => startEdit(ev)}
                            data-testid={`events-edit-${ev.id}`}
                          >
                            <Edit3 size={14} /> Edit
                          </button>
                          <button
                            className="btn-ghost btn-danger"
                            onClick={() => removeEvent(ev.id)}
                            data-testid={`events-delete-${ev.id}`}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {events.some(isPastEvent) && (
          <section className="events-history-section" data-testid="events-history">
            <h2 className="events-list-title">History</h2>
            <div className="events-history-scroll">
              {events.filter(isPastEvent).map((ev) => (
                <div className="events-history-circle" key={ev.id} data-testid={`event-history-${ev.id}`}>
                  {ev.hero_image_url && <img src={ev.hero_image_url} alt="" className="events-history-bg" />}
                  <div className="events-history-inner">
                    <div className="events-history-title">{ev.title}</div>
                    <div className="events-history-date">
                      {ev.starts_at ? new Date(ev.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                    </div>
                    {(ev.venue || ev.location_display || ev.location) && (
                      <div className="events-history-loc">{ev.venue || ev.location_display || ev.location}</div>
                    )}
                    {(ev.rsvps || []).length > 0 && (
                      <div className="events-history-rsvp">{(ev.rsvps || []).length} went</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default Events;
